import type { BeadColor } from '../catalog/types';
import {
  computePlateLayout,
  estimatedTimeMinutes,
  physicalSize,
  type PlateLayout,
} from './estimate';
import type { Pattern } from './pipeline';

/**
 * Crée un `Pattern` vierge entièrement rempli d'une seule couleur.
 *
 * Utilisé pour le mode "création vierge" (F2 du cahier des charges) : l'utilisateur
 * démarre avec une grille uniforme et peint dessus.
 *
 * @throws si la palette est vide, si `baseColorId` n'est pas dans la palette,
 *   ou si les dimensions sont invalides.
 */
export function makeBlankPattern(args: {
  width: number;
  height: number;
  palette: ReadonlyArray<BeadColor>;
  baseColorId: string;
  plate?: { width: number; height: number };
}): Pattern {
  const { width, height, palette, baseColorId } = args;
  if (palette.length === 0) {
    throw new Error('makeBlankPattern : palette vide.');
  }
  if (width <= 0 || height <= 0 || !Number.isInteger(width) || !Number.isInteger(height)) {
    throw new Error(`makeBlankPattern : dimensions invalides ${width}×${height}.`);
  }
  const baseIdx = palette.findIndex((c) => c.id === baseColorId);
  if (baseIdx < 0) {
    throw new Error(`makeBlankPattern : couleur '${baseColorId}' absente de la palette.`);
  }

  const N = width * height;
  const cells = new Array<number>(N).fill(baseIdx);
  const paletteColorIds = palette.map((c) => c.id);

  const counts = new Map<string, number>();
  for (const c of palette) counts.set(c.id, 0);
  counts.set(baseColorId, N);

  const plateW = args.plate?.width ?? 29;
  const plateH = args.plate?.height ?? 29;
  const layout: PlateLayout = computePlateLayout(width, height, plateW, plateH);

  return {
    width,
    height,
    cells,
    paletteColorIds,
    counts,
    layout,
    physicalSize: physicalSize(width, height),
    estimatedTimeMinutes: estimatedTimeMinutes(N),
  };
}
